import { Box } from '@chakra-ui/react';
import { forwardRef, useCallback } from 'react';
import { Mention, MentionsInput } from 'react-mentions'

export const defaultStyle = {
    control: {
        fontWeight: 'normal'
    },

    '&multiLine': {
        width: '100%',
        borderRadius: '5px',

        control: {
            fontFamily: 'var(--code-font-family)'
        },
        highlighter: {
            padding: 8,
            paddingLeft: 16,
        },
        input: {
            padding: 8,
            paddingLeft: 16,
            borderColor: 'transparent',
            borderRadius: '5px',
            borderWidth: '1px'
        },
    },

    suggestions: {
        fontFamily: 'var(--code-font-family)',
        zIndex: 4,

        list: {
            backgroundColor: 'var(--chakra-colors-secondary-background)',
            border: '1px solid rgba(0,0,0,0.15)',
        },
        item: {
            textAlign: 'left',
            padding: '5px 15px',
            borderBottom: '1px solid rgba(0,0,0,0.15)',
            '&focused': {
                backgroundColor: 'var(--chakra-colors-selected)',
            },
        },
    },
}

export const defaultSuggestionStyle = { 
    backgroundColor: 'var(--chakra-colors-suggestion-highlight)',
    borderRadius: '2px',
    padding: '2px',
    margin: '-2px'
}

const ENQ = '\u0005'

const MARKED_TAG_REGEX = new RegExp(`@([\\w]+)${ENQ}`)

const normalize = (value) => value.replaceAll(ENQ, '')

export const FIXFieldsAutocompleteInput = 
    forwardRef(({
        fieldSuggestions,
        value,
        onChange,
        onBlur = undefined,
        onFocus = undefined,
        placeholder = '',
        optionsLimit = 10,
        style = defaultStyle,
        suggestionStyle = defaultSuggestionStyle
    }, ref) => {

    const suggest = useCallback(
        (query) => {
            const queryLowerCase = query.toLowerCase()
            const results = []
            for (const fieldNameLowerCase in fieldSuggestions) {
                const fieldSuggestion = fieldSuggestions[fieldNameLowerCase]
                const indexOf = fieldSuggestion.displayLowerCase.indexOf(queryLowerCase)
                if (indexOf >= 0) {
                    results.push({ indexOf, fieldSuggestion })
                }
            }

            return results.sort((a, b) => a.indexOf - b.indexOf).map((e) => e.fieldSuggestion)
        },
        [fieldSuggestions])

    const mark = useCallback(
        (query) => {
            const regex = new RegExp(`@([\\w]+)(?:$|[^${ENQ}\\w])`, 'g');
            
            var markedQuery = query;
            [...query.matchAll(regex)].forEach(result => {
                const potentialField = result[1]
                const field = fieldSuggestions[potentialField.toLowerCase()]
                if (field) {
                    const regex = new RegExp(`@${potentialField}($|[^${ENQ}\\w])`, 'g')
                    markedQuery = markedQuery.replace(regex, `@${field.name}${ENQ}$1`)
                }
            })
            return markedQuery
        },
        [fieldSuggestions])

    return <Box
        width='100%'
        boxShadow='0 0 0 1px var(--chakra-colors-border) inset'
        backgroundColor='var(--chakra-colors-input)'
        borderRadius='5px'
        _hover={{
        boxShadow: '0 0 0 1px var(--chakra-colors-border-hover) inset'
        }}
    >
        <MentionsInput
            inputRef={ref}
            value={mark(value)}
            onBlur={onBlur}
            onFocus={onFocus}
            onChange={(e) => {
                const value = e.target.value
                if (!value.trim()) {
                    onChange('')
                } else {
                    onChange(normalize(e.target.value))
                }
            }}
            style={style}
            placeholder={placeholder}
            a11ySuggestionsListLabel="Suggested field names"
            
            // customSuggestionsContainer is not supported in @types/react-mentions yet
            // @ts-ignore 
            customSuggestionsContainer={(children) => {
                const elements = children.length > optionsLimit ? children.slice(0,optionsLimit) : children
                return (
                    <div>
                        {elements}
                        {children.length > optionsLimit &&
                            <div             
                                style={style.suggestions.item}
                            >{children.length - 10} more...</div>
                        }
                    </div>
                )
            }}
        >
            <Mention
                trigger="@"
                data={suggest}
                displayTransform={(id) => `@${id}`}
                markup={'@__id__' + ENQ}
                appendSpaceOnAdd
                regex={MARKED_TAG_REGEX}
                
                style={suggestionStyle}
                renderSuggestion={
                    (
                        suggestion,
                        search,
                        highlightedDisplay,
                        index,
                        focused
                    ) => {
                        return <div className={`suggestion ${focused ? 'focused' : ''}`}>
                            {highlightedDisplay}
                            <span style={{
                                color: 'gray'
                            }}> {suggestion.type}</span>
                        </div>
                    }
                }
            />
        </MentionsInput>
    </Box>
})